/**
 * Article of the Week — hero card on the Home page.
 * Pulls the most-recent (or manually-featured) socialpulse.com article.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Newspaper, ArrowUpRight } from "lucide-react";

export default function ArticleOfTheWeek() {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/sp-articles/featured/current")
      .then((r) => setArticle(r.data.article))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !article) return null;

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12"
      data-testid="article-of-the-week"
    >
      <Link
        to={`/articles/${article.slug}`}
        className="group grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden border shadow-sm hover:shadow-xl transition-shadow"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-paper)" }}
      >
        {article.feature_image && (
          <div className="relative aspect-[16/10] md:aspect-auto overflow-hidden">
            <img
              src={article.feature_image}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
        )}
        <div className="p-8 md:p-10 flex flex-col justify-center">
          <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>
            <Newspaper size={13} className="inline mr-1.5 -mt-0.5" /> ARTICLE OF THE WEEK
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-3">
            {article.title}
          </h2>
          <div className="text-xs mb-4" style={{ color: "var(--wofy-text-soft)" }}>
            {article.published_at ? new Date(article.published_at).toLocaleDateString(undefined, { dateStyle: "long" }) : ""}
            {article.author && ` · ${article.author}`}
          </div>
          {article.excerpt && (
            <p className="text-sm mb-6 max-w-lg" style={{ color: "var(--wofy-text-soft)" }}>
              {article.excerpt}
            </p>
          )}
          <div className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: "var(--wofy-primary)" }}>
            Read the full story <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </Link>
    </section>
  );
}
